import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Calendar,
  Clock,
  BarChart3,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Timer,
  RefreshCw,
  FileText
} from 'lucide-react';
import AttendanceCalendar from './AttendanceCalendar';
import PunchButton from './PunchButton';
import LeaveManagement from './LeaveManagement';
import { attendanceApi } from '../../services/api/attendance';
import { mockAttendanceStats } from '../../services/mockData';
import { AttendanceProvider, useAttendanceContext } from '../../contexts/AttendanceContext';
import toast from 'react-hot-toast';

interface AttendanceStats {
  monthly: {
    totalWorkingDays: number;
    presentDays: number;
    absentDays: number;
    leaveDays: number;
    holidayDays: number;
    partialDays: number;
    totalWorkingHours: number;
    averageWorkingHours: number;
  };
  weekly: {
    totalWorkingDays: number;
    presentDays: number;
    absentDays: number;
    leaveDays: number;
    holidayDays: number;
    partialDays: number;
    totalWorkingHours: number;
    averageWorkingHours: number;
  };
}

interface AttendanceDashboardProps {
  userId?: string;
  isManager?: boolean;
}

// Internal component that uses the context
const AttendanceDashboardContent: React.FC<AttendanceDashboardProps> = ({
  userId,
  isManager = false,
}) => {
  const [stats, setStats] = useState<AttendanceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const { refreshTrigger } = useAttendanceContext();

  // Fetch attendance statistics
  const fetchStats = async () => {
    try {
      setLoading(true);

      console.log('🔄 Fetching attendance stats for userId:', userId);

      const response = await attendanceApi.getAttendanceStats({ userId });
      console.log('✅ Attendance stats response:', response);

      // Check if response has the expected structure
      if (!response || (!response.monthly && !response.weekly)) {
        console.warn('⚠️ Invalid stats response structure:', response);
        throw new Error('Invalid response structure');
      }

      setStats(response);
    } catch (error: any) {
      console.error('Error fetching stats:', error);

      // More specific error handling
      if (error.response?.status === 401) {
        toast.error('Please log in to view attendance statistics');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view this data');
      } else if (error.response?.status === 404) {
        toast.error('No attendance data found');
      } else {
        toast.error('Failed to load attendance statistics. Please try again.');
      }

      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [userId, refreshTrigger]); // Add refreshTrigger to dependencies

  // Initialize with mock data if no stats are available
  useEffect(() => {
    if (!stats && !loading) {
      console.log('🔄 No stats available, using mock data as fallback');
      setStats(mockAttendanceStats);
    }
  }, [stats, loading]);

  // Calculate attendance percentage
  const calculateAttendancePercentage = (present: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((present / total) * 100);
  };

  // Format hours
  const formatHours = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Stats card component
  const StatsCard = ({ 
    title, 
    value, 
    subtitle, 
    icon: Icon, 
    color = 'blue',
    trend 
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: any;
    color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple';
    trend?: 'up' | 'down' | 'neutral';
  }) => {
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
    };

    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <p className="text-2xl font-bold">{value}</p>
              {subtitle && (
                <p className="text-xs text-muted-foreground">{subtitle}</p>
              )}
            </div>
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="h-4 w-4" />
            </div>
          </div>
          {trend && (
            <div className="mt-2 flex items-center">
              <TrendingUp className={`h-3 w-3 mr-1 ${
                trend === 'up' ? 'text-green-500' : 
                trend === 'down' ? 'text-red-500' : 'text-gray-500'
              }`} />
              <span className="text-xs text-muted-foreground">
                vs last period
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Attendance Dashboard</h1>
          <p className="text-muted-foreground">
            {isManager ? 'Team attendance overview' : 'Your attendance overview'}
          </p>

        </div>
        <div className="flex items-center space-x-3">
          {/* Punch Button - only show for non-managers or when viewing own attendance */}
          {(!isManager || !userId) && (
            <PunchButton userId={userId} />
          )}
          <Button
            onClick={() => {
              console.log('Apply Leave button clicked, switching to leaves tab');
              // Find and click the leaves tab trigger
              const leavesTabTrigger = tabsRef.current?.querySelector('[data-value="leaves"], button[value="leaves"]') as HTMLButtonElement;
              if (leavesTabTrigger) {
                leavesTabTrigger.click();
              } else {
                // Fallback: find by text content
                const buttons = tabsRef.current?.querySelectorAll('button');
                buttons?.forEach(button => {
                  if (button.textContent?.includes('Leaves')) {
                    button.click();
                  }
                });
              }
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <FileText className="h-4 w-4 mr-2" />
            Apply Leave
          </Button>
          <Button onClick={fetchStats} disabled={loading} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div ref={tabsRef}>
        <Tabs defaultValue="overview">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
            <TabsTrigger value="leaves">Leaves</TabsTrigger>
          </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Loading State */}
          {loading && !stats && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Timer className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">Loading attendance statistics...</p>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          {stats && (
            <>
              <div>
                <h3 className="text-lg font-semibold mb-4">This Month</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatsCard
                    title="Attendance Rate"
                    value={`${calculateAttendancePercentage(
                      stats.monthly.presentDays,
                      stats.monthly.totalWorkingDays
                    )}%`}
                    subtitle={`${stats.monthly.presentDays}/${stats.monthly.totalWorkingDays} days`}
                    icon={CheckCircle}
                    color="green"
                  />
                  <StatsCard
                    title="Total Hours"
                    value={formatHours(stats.monthly.totalWorkingHours)}
                    subtitle={`Avg: ${formatHours(stats.monthly.averageWorkingHours)}/day`}
                    icon={Clock}
                    color="blue"
                  />
                  <StatsCard
                    title="Absent Days"
                    value={stats.monthly.absentDays}
                    subtitle="This month"
                    icon={XCircle}
                    color="red"
                  />
                  <StatsCard
                    title="Leave Days"
                    value={stats.monthly.leaveDays}
                    subtitle="Approved leaves"
                    icon={Calendar}
                    color="purple"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">This Week</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatsCard
                    title="Attendance Rate"
                    value={`${calculateAttendancePercentage(
                      stats.weekly.presentDays,
                      stats.weekly.totalWorkingDays
                    )}%`}
                    subtitle={`${stats.weekly.presentDays}/${stats.weekly.totalWorkingDays} days`}
                    icon={CheckCircle}
                    color="green"
                  />
                  <StatsCard
                    title="Total Hours"
                    value={formatHours(stats.weekly.totalWorkingHours)}
                    subtitle={`Avg: ${formatHours(stats.weekly.averageWorkingHours)}/day`}
                    icon={Clock}
                    color="blue"
                  />
                  <StatsCard
                    title="Partial Days"
                    value={stats.weekly.partialDays}
                    subtitle="Incomplete attendance"
                    icon={AlertCircle}
                    color="yellow"
                  />
                  <StatsCard
                    title="Holiday Days"
                    value={stats.weekly.holidayDays}
                    subtitle="This week"
                    icon={Calendar}
                    color="purple"
                  />
                </div>
              </div>
            </>
          )}

          {/* Attendance Breakdown */}
          {stats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5" />
                  <span>Monthly Breakdown</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-green-100 text-green-800">Present</Badge>
                      <span className="text-sm">{stats.monthly.presentDays} days</span>
                    </div>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{
                          width: `${(stats.monthly.presentDays / stats.monthly.totalWorkingDays) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-red-100 text-red-800">Absent</Badge>
                      <span className="text-sm">{stats.monthly.absentDays} days</span>
                    </div>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-red-500 h-2 rounded-full"
                        style={{
                          width: `${(stats.monthly.absentDays / stats.monthly.totalWorkingDays) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-blue-100 text-blue-800">Leave</Badge>
                      <span className="text-sm">{stats.monthly.leaveDays} days</span>
                    </div>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{
                          width: `${(stats.monthly.leaveDays / stats.monthly.totalWorkingDays) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-yellow-100 text-yellow-800">Partial</Badge>
                      <span className="text-sm">{stats.monthly.partialDays} days</span>
                    </div>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-yellow-500 h-2 rounded-full"
                        style={{
                          width: `${(stats.monthly.partialDays / stats.monthly.totalWorkingDays) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Stats Available */}
          {!loading && !stats && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500 mb-4">Unable to load attendance statistics</p>
                <button
                  onClick={fetchStats}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <AttendanceCalendar
            userId={userId}
            showUserSelector={isManager}
            height={700}
          />
        </TabsContent>

        <TabsContent value="leaves">
          <LeaveManagement isManager={isManager} />
        </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Main component - now uses the context provided at layout level
const AttendanceDashboard: React.FC<AttendanceDashboardProps> = (props) => {
  return <AttendanceDashboardContent {...props} />;
};

export default AttendanceDashboard;
